import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Sparkles, Scale, Handshake, Wallet, ShieldAlert, CheckCircle2, MinusCircle, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const bandStyle = (b) => b === "low" ? { color: "text-emerald-700", Icon: CheckCircle2, bg: "bg-emerald-500" }
  : b === "medium" ? { color: "text-amber-700", Icon: MinusCircle, bg: "bg-amber-500" }
  : { color: "text-red-700", Icon: XCircle, bg: "bg-red-500" };

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function BuyerAdvisor() {
  const { user } = useAuth();
  const [verified, setVerified] = useState([]);
  const [selection, setSelection] = useState([]);
  const [neg, setNeg] = useState(null);
  const [tco, setTco] = useState(null);
  const [risk, setRisk] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [q, setQ] = useState("What is the best value pick in my shortlist?");
  const [ans, setAns] = useState("");
  const [asking, setAsking] = useState(false);
  const [activeVehicle, setActiveVehicle] = useState(null);

  useEffect(() => { (async () => {
    const r = await api.get("/vehicles", { params: { only_verified: true, limit: 30 } });
    setVerified(r.data);
  })(); }, []);

  const chooseVehicle = async (vid) => {
    setActiveVehicle(vid);
    setNeg(null); setTco(null); setRisk(null);
    try {
      const [n, t, r] = await Promise.all([
        api.get(`/advisor/negotiation/${vid}`),
        api.get(`/advisor/tco/${vid}`),
        api.get(`/advisor/risk/${vid}`),
      ]);
      setNeg(n.data); setTco(t.data); setRisk(r.data);
    } catch { toast.error("Failed to load advisor data"); }
  };

  const toggleSelection = (vid) => {
    setSelection(s => s.includes(vid) ? s.filter(x => x !== vid) : (s.length < 4 ? [...s, vid] : s));
  };

  const runCompare = async () => {
    if (selection.length < 2) { toast("Select 2-4 vehicles to compare"); return; }
    try { const r = await api.post("/advisor/compare", selection); setComparison(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const askAI = async () => {
    if (!user) { toast("Please sign in to use the AI advisor"); return; }
    if (!q.trim()) return;
    setAsking(true); setAns("");
    try {
      const r = await api.post("/advisor/ask", { question: q, vehicle_ids: selection });
      setAns(r.data.answer);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAsking(false); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-8 max-w-3xl">
          <span className="thom-seal"><Sparkles className="h-3 w-3" /> THOM Buyer Advisor™</span>
          <h1 className="display text-4xl md:text-5xl mt-3">Your evidence-based purchase advisor.</h1>
          <p className="text-muted-foreground mt-2">
            Ask questions in plain language, compare verified vehicles side-by-side, get negotiation guidance, and see the total cost you'll actually pay over five years — powered by verified platform data, not marketing text.
          </p>
        </div>

        <Tabs defaultValue="advise">
          <TabsList>
            <TabsTrigger value="advise" data-testid="adv-tab-advise"><Sparkles className="h-3.5 w-3.5 mr-1" /> Ask AI</TabsTrigger>
            <TabsTrigger value="compare" data-testid="adv-tab-compare"><Scale className="h-3.5 w-3.5 mr-1" /> Compare</TabsTrigger>
            <TabsTrigger value="single" data-testid="adv-tab-single"><Handshake className="h-3.5 w-3.5 mr-1" /> Analyse One Vehicle</TabsTrigger>
          </TabsList>

          <TabsContent value="advise">
            <div className="grid lg:grid-cols-2 gap-6 mt-4">
              <div className="border border-border bg-card p-6">
                <div className="kicker mb-2">Ask the Buyer Advisor</div>
                <Textarea rows={4} value={q} onChange={(e) => setQ(e.target.value)} placeholder='e.g. "Should I buy the Prado or the Hilux?"' data-testid="adv-question" />
                <div className="mt-2 text-xs text-muted-foreground">Tip: pick specific vehicles in the Compare tab first — the advisor will use them as context.</div>
                <Button onClick={askAI} disabled={asking} className="mt-3 bg-primary hover:bg-primary/90" data-testid="adv-ask-btn">
                  {asking ? "Thinking…" : "Ask the advisor"}
                </Button>
              </div>
              <div className="border border-primary/30 bg-primary/[0.04] p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <div className="kicker text-primary">Answer</div>
                </div>
                {ans ? (
                  <p className="text-sm leading-relaxed" data-testid="adv-answer">{ans}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Ask a question to receive an evidence-based recommendation. The advisor never guarantees future performance.</p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="compare">
            <div className="mt-4">
              <div className="kicker mb-2">Select 2–4 verified vehicles</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {verified.map(v => (
                  <button key={v.id} onClick={() => toggleSelection(v.id)}
                          className={`text-left border p-3 transition-colors ${selection.includes(v.id) ? "border-primary bg-primary/[0.06]" : "border-border bg-card hover:border-primary"}`}
                          data-testid={`adv-select-${v.id}`}>
                    <div className="text-sm font-semibold">{v.year} {v.make} {v.model}</div>
                    <div className="mono text-xs text-muted-foreground">Trust {v.trust_score ?? "—"} · {money(v.price_usd)} · {v.city}</div>
                  </button>
                ))}
              </div>
              <Button onClick={runCompare} className="mt-4 bg-primary hover:bg-primary/90" data-testid="adv-compare-run">Run comparison</Button>

              {comparison && (
                <div className="mt-6 border border-border bg-card overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/60">
                      <tr>
                        <th className="text-left py-2 px-3">Attribute</th>
                        {comparison.vehicles.map((v, i) => <th key={i} className="text-left py-2 px-3">{v.title}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.rows.map(row => (
                        <tr key={row.key} className="border-t border-border">
                          <td className="py-2 px-3 text-muted-foreground">{row.label}</td>
                          {row.values.map((val, i) => (
                            <td key={i} className={`py-2 px-3 mono ${row.best_index === i ? "text-primary font-semibold" : ""}`}>
                              {val == null ? "—" : typeof val === "number" && row.key.includes("usd") ? money(val) : String(val)}
                              {row.best_index === i && <span className="ml-2 mono text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded-sm">BEST</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="single">
            <div className="mt-4">
              <div className="kicker mb-2">Pick a verified vehicle to analyse</div>
              <Select value={activeVehicle || ""} onValueChange={chooseVehicle}>
                <SelectTrigger className="max-w-md" data-testid="adv-single-select"><SelectValue placeholder="Choose vehicle" /></SelectTrigger>
                <SelectContent>
                  {verified.map(v => <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} · Trust {v.trust_score ?? "—"}</SelectItem>)}
                </SelectContent>
              </Select>

              {activeVehicle && (
                <div className="mt-6 grid lg:grid-cols-3 gap-4">
                  {/* Negotiation */}
                  {neg && (
                    <div className="border border-border bg-card p-5">
                      <div className="flex items-center gap-2 mb-2"><Handshake className="h-4 w-4 text-primary" /><div className="kicker">Negotiation Range</div></div>
                      <div className="display text-3xl">{money(neg.recommended_low)} – {money(neg.recommended_high)}</div>
                      <div className="text-xs text-muted-foreground mt-1">Asking {money(neg.asking)} · Market {money(neg.market)}</div>
                      <ul className="mt-3 space-y-1 text-xs">
                        {neg.reasons.map((r, i) => <li key={i} className="flex items-start gap-2"><Sparkles className="h-3 w-3 mt-0.5 text-primary" /> {r}</li>)}
                      </ul>
                    </div>
                  )}
                  {/* TCO */}
                  {tco && (
                    <div className="border border-border bg-card p-5">
                      <div className="flex items-center gap-2 mb-2"><Wallet className="h-4 w-4 text-primary" /><div className="kicker">Total Cost of Ownership</div></div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div><div className="display text-xl">{money(tco.total_1yr_usd)}</div><div className="text-[11px] text-muted-foreground">1 year</div></div>
                        <div><div className="display text-xl">{money(tco.total_3yr_usd)}</div><div className="text-[11px] text-muted-foreground">3 years</div></div>
                        <div><div className="display text-xl">{money(tco.total_5yr_usd)}</div><div className="text-[11px] text-muted-foreground">5 years</div></div>
                      </div>
                      <div className="mt-3 text-xs space-y-1">
                        {Object.entries(tco.annual).map(([k, v]) => (
                          <div key={k} className="flex justify-between border-b border-border py-1"><span className="text-muted-foreground">{k.replace(/_usd/g, "").replace(/_/g, " ")} /yr</span><span className="mono">{money(v)}</span></div>
                        ))}
                      </div>
                      <div className="mt-2 text-[10px] mono text-muted-foreground">assumes {tco.assumptions.annual_km.toLocaleString()} km/year · {tco.assumptions.fuel_type}</div>
                    </div>
                  )}
                  {/* Risk */}
                  {risk && (
                    <div className="border border-border bg-card p-5">
                      <div className="flex items-center gap-2 mb-2"><ShieldAlert className="h-4 w-4 text-primary" /><div className="kicker">Buyer Risk Dashboard</div></div>
                      <div className="flex items-baseline gap-3">
                        <div className="display text-4xl text-primary">{risk.overall.score}</div>
                        <div className={`text-sm uppercase mono ${bandStyle(risk.overall.band).color}`}>{risk.overall.band} risk</div>
                      </div>
                      <div className="mt-3 space-y-2 text-xs">
                        {Object.entries(risk.categories).map(([k, v]) => {
                          const bs = bandStyle(v.band);
                          return (
                            <div key={k} className="flex items-center gap-2">
                              <div className="w-24 text-muted-foreground uppercase mono text-[10px]">{k}</div>
                              <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
                                <div className={`h-full ${bs.bg}`} style={{ width: `${v.score}%` }} />
                              </div>
                              <div className={`mono w-8 text-right ${bs.color}`}>{v.score}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
